import React, { useState } from "react";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";
import { Button, Card, FormGroup, Map } from "@cofano/cck";
import { Box, Grid2 as Grid, Tab, Tabs, Tooltip } from "@mui/material";
import LinkOffSharpIcon from "@mui/icons-material/LinkOffSharp";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShip } from "@fortawesome/pro-duotone-svg-icons";
import { useConfirmDeleteVoyage } from "view/voyages/hooks/useConfirmDeleteVoyage.tsx";
import LastShippedProducts from "components/products/LastShippedProducts";
import { CustomDialog } from "components/dialogs/Dialogs";
import mapStyles from "view/maps/utils/Styles.json";
import NominationStatusChanger from "view/charters/components/status/NominationStatusChanger";
import { hasPluginPermission, Language, usePermission } from "imports/Utils";
import { calculateMaxWeightAndVolume } from "utils/cargoActionUtils";
import { RelationLink, ShipLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";
import { DetailsBlock, Height } from "../Blocks";
import { SalesCharter, Voyage } from "types/index";
import LastShippedProductsTable from "view/ships/components/table/LastShippedProductsTable";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { BargeType, CharterPdfType } from "__generated__/enums.ts";
import DocumentDrawer from "components/documents/DocumentDrawer.tsx";
import ShipMarker from "view/maps/components/markers/ShipMarker";
import PlanningChecks from "components/planningChecks/PlanningChecks";

type ShipDetailsBlockProps = {
	charter: SalesCharter;
	voyage: Voyage;
	color: string;
	refetch: () => void;
};

enum ShipDetailsTab {
	Details,
	PreviousCargos
}

const ShipDetailsBlock = ({ charter, voyage, color, refetch }: ShipDetailsBlockProps) => {

	const session = useStoreState(store => store.session);

	const { loading, confirmUnplanCargos, confirmDeleteVoyage } = useConfirmDeleteVoyage({ refetch });

	const plannedVoyage = {
		charterId: charter.id,
		voyageId: voyage.id,
		cargoIds: voyage.cargos.map((c) => c.id),
		purchaseCharter: charter.purchaseCharter,
	};

	const { isCustomer, isTerminal, isReadOnly } = usePermission();

	const [ selectedTab, selectTab ] = useState(ShipDetailsTab.Details);
	const [ dialogOpen, openDialog ] = useState(false);

	const [ maxWeight, maxVolume ] = charter.cargos ? calculateMaxWeightAndVolume(charter.cargos[0]) : [ 0, 0 ];

	return (
		<>
			<DetailsBlock height={Height.Fill}>
				<Card
					color={color}
					icon={<FontAwesomeIcon icon={faShip} style={{ color: color, height: 22, width: 22 }} />}
					title={<ShipLink ship={voyage.ship} />}
					subtitle={<RelationLink relation={voyage.creditor} />}
					actions={[
						{
							hidden: !charter.lastShipNominationStatus,
							render: () => (
								<span style={{ marginRight: 4 }}>
									<NominationStatusChanger
										// @ts-expect-error
										charter={charter}
										refetch={refetch}
									/>
								</span>
							),
						},
						{ render: () => <PdfButton charter={charter} type={CharterPdfType.NominationToShipOwner} /> },
						{
							icon: <LinkOffSharpIcon fontSize="small" />,
							hidden: isReadOnly,
							disabled: loading,
							onClick: () => confirmUnplanCargos(plannedVoyage),
						}, {
							icon: <DeleteIcon fontSize="small" />,
							hidden: isReadOnly,
							disabled: loading,
							onClick: () => confirmDeleteVoyage(plannedVoyage),
						} ]}
					disablePadding>
					<>
						<Box sx={{ borderTop: 1, borderBottom: 1, borderColor: "divider", marginBottom: 1 }}>
							<Tabs value={selectedTab} onChange={(event, newValue) => selectTab(newValue)}>
								<Tab label={Language("DETAILS")} value={ShipDetailsTab.Details} />

								{voyage.ship.bargeType === BargeType.Tanker && (
									<Tab label={Language("PREVIOUS_CARGOS")} value={ShipDetailsTab.PreviousCargos} />
								)}
							</Tabs>
						</Box>

						<div>
							{selectedTab === ShipDetailsTab.Details && (
								<>
									{hasPluginPermission(session, "angler") && !voyage.expired && !voyage.finished && <PlanningChecks voyageId={voyage.id} />}
									<Grid container spacing={3} style={{ padding: "8px 16px" }}>
										{hasPluginPermission(session, "showLastShippedProducts") && (!isCustomer || !isTerminal) && (
											<Grid size={{ xs: 12, lg: 6 }}>
												<LastShippedProducts
													labelPlacement="top"
													voyage={voyage}
													ship={voyage.ship}
													refetch={refetch}
													charterId={charter.id}
												/>
											</Grid>
										)}

										<Grid size={{ xs: 12, lg: 6 }}>
											<div style={{ paddingTop: 8, marginBottom: 8 }}>
												<FormGroup
													label={Language("SHIPS_DIMENSIONS")}
													labelPlacement="left-center"
													overflow="hidden">
													{`${(voyage.ship.lengthCm || 0) / 100}m x ${(voyage.ship.widthCm || 0) / 100}m`}
												</FormGroup>
											</div>

											<FormGroup label={Language("CAPACITY_TON")} labelPlacement="left-center" overflow="hidden">
												<ShipCapacity
													max={maxWeight}
													capacity={voyage.ship.capacityTon || 0}
													unit={intl.formatMessage({ id: "UNIT_TON" }).toLowerCase()} />
											</FormGroup>

											<FormGroup label={Language("CAPACITY_M3")} labelPlacement="left-center" overflow="hidden">
												<ShipCapacity
													max={maxVolume}
													capacity={voyage.ship.capacityM3 || 0}
													unit={intl.formatMessage({ id: "CARGO_VOLUME" }).toLowerCase()} />
											</FormGroup>

											<FormGroup
												label={Language("ANGLER_CURRENT_POSITION")}
												labelPlacement="left"
												labelPaddingTop={0}
												overflow="hidden">
												{voyage.ship.position?.location && (
													<div>
														<div>
															{voyage.ship.position.location}
														</div>

														<div>
															<FormattedDate
																date={voyage.ship.position.locationEntered}
																withoutYear />
														</div>

														{voyage.ship.position.latitude && (
															<Button
																onClick={() => openDialog(true)}
																style={{ marginTop: 8 }}
																variant="outlined"
																size="small">
																{intl.formatMessage({ id: "MAPS_POSITIONS" })}
															</Button>
														)}
													</div>
												)}
											</FormGroup>
										</Grid>
									</Grid>
								</>
							)}

							{selectedTab === ShipDetailsTab.PreviousCargos && (
								<div style={{ height: "100%", maxHeight: 250, overflowY: "auto" }}>
									<LastShippedProductsTable
										ship={voyage.ship}
										filterOnCharterId={charter.id}
									/>
								</div>
							)}
						</div>
					</>
				</Card>
			</DetailsBlock>
			<CustomDialog
				open={dialogOpen}
				onClose={() => openDialog(false)}
				title={intl.formatMessage({ id: "MAPS_POSITIONS" })}>
				<Map
					apiKey={"AIzaSyAFEhlhSJ7c85rDGz1JX99veD0sshXx_xs"}
					className="ship-details-position-map"
					styles={mapStyles}
					markers={[ { lat: voyage.ship.position?.latitude || 0, lng: voyage.ship.position?.longitude || 0 } ]}
					markerComponent={() => (
						<ShipMarker
							course={voyage.ship.position?.course}
							actual={false} />
					)} />
			</CustomDialog>
		</>
	);
};

const ShipCapacity = ({ max = 0, capacity = 0, unit }: { max: number, capacity: number, unit: string }) => {

	const valid = (max <= capacity);

	return (
		<div style={{ alignItems: "center", display: "flex" }}>
			<span style={{ marginRight: 4 }}>
				{`${max.toFixed(1)} / ${capacity || "-"} ${unit}`}
			</span>

			{valid ? (
				<CheckCircleOutlineOutlinedIcon
					color="success"
					fontSize="small"
				/>
			) : (
				<HighlightOffOutlinedIcon
					color="error"
					fontSize="small"
				/>
			)}
		</div>
	);
};

export type PdfButtonProps = {
	charter: Pick<SalesCharter, "id">,
	type: Exclude<CharterPdfType, CharterPdfType.CargoDocuments>,
	includePricing?: boolean;
	isNomination?: boolean;
	disabled?: boolean;
};
export const PdfButton = ({ charter, type, includePricing, isNomination = false, disabled = false }: PdfButtonProps) => {

	const [ drawerOpen, openDrawer ] = useState(false);

	return (
		<React.Fragment>
			<Tooltip title={intl.formatMessage({ id: `PREVIEW_EXPORT_${type}` })}>
				<span>
					<Button
						onClick={() => openDrawer(true)}
						variant="text"
						size="small"
						disabled={disabled}
					>
						<InsertDriveFileIcon fontSize="small" />
					</Button>
				</span>
			</Tooltip>

			<DocumentDrawer
				open={drawerOpen}
				onClose={() => openDrawer(false)}
				url={`/finance/charters/pdf/${charter.id}/export`}
				queryParams={{ "pdfType": type, "includePricing": (includePricing ?? true).toString(), "isNomination": (isNomination ?? false).toString() }}
				multilingual
			/>
		</React.Fragment>
	);
};

export default ShipDetailsBlock;
